import { useState, useEffect, useMemo } from "react";
import { Tag } from "primereact/tag";
import { motion, AnimatePresence } from "framer-motion";
import { formatarMoedaBrasileira } from "../../utils/formatarMoedaBrasileira";
import { Dialog } from "primereact/dialog";
import Button from "../../componentes/button/button";
import TabelaComissao from "./TabelaComissao";
import TabelaCargaEntrega from "./TabelaCargaEntrega";
import TabelaTelemetria from "./TabelaTelemetria";
import TabelaAfastamentos from "./TabelaAfastamentos";
import ParametrosSnapshotView from "./ParametrosSnapshotView";
import TabelaConcept from "./TabelaConcept";
import TabelaRomaneios from "./TabelaRomaneios";
import ComissaoCaxiasService from "../../services/resource/ComissaoCaxiasService";
import { useAuth } from "../../context/authContext";
import LogService from "../../services/resource/logService";
import { formatLocalDateTime, returnDate } from "../../utils/formatDate";
import FilialService from "../../services/resource/filialService";
import TabDiasImportados from "./TabDiasImportados";
import ComissaoService from "../../services/resource/ComissaoService";
import { GerarComissoes } from "./GerarComissoes";
import { ANOS, MESES } from "../../utils/options";
import DashboardTab from "./TabDashboard";
import RankingTab from "./TabRanking";
import ExportacaoTab from "./TabExportacao";

const formatPercent = (value) =>
    value != null ? `${Number(value).toFixed(2)}%` : "—";

const safeNumber = (value) =>
    value != null ? Number(value).toFixed(2) : "—";

const formatarValorPorCubo = (value) =>
    value != null ? Number(value).toFixed(3).replace(".", ",") : "—";

const formatarDataIso = (iso) =>
    iso ? String(iso).substring(0, 10).split("-").reverse().join("/") : "—";

// Detalhamento do cálculo gravado pelo backend (null em comissões geradas antes dessa coluna existir)
const lerAuditoria = (json) => {
    if (!json) return null;
    try {
        return JSON.parse(json);
    } catch (error) {
        console.error("JSON inválido em auditoriaJson", error);
        return null;
    }
};

// Base avaliada de um indicador (retorno | conclusao | telemetria): soma das funções exercidas. O backend já
// retirou os dias/escalas com ajuste aprovado; aqui só se apresenta.
const baseDoIndicador = (auditoria, chave) => {
    const bases = (auditoria?.segmentos ?? []).map(s => s[chave]?.baseAvaliacao).filter(Boolean);
    if (!bases.length) return null;
    return {
        diasAvaliaveis: bases.reduce((a, b) => a + (b.diasAvaliaveis ?? 0), 0),
        diasExcluidos: [...new Set(bases.flatMap(b => b.datasExcluidasPorAjuste ?? []))],
    };
};

const INDICADOR_COR = {
    RETORNO: "bg-blue-50 text-blue-700 border-blue-200",
    CONCLUSAO_GREENMILE: "bg-purple-50 text-purple-700 border-purple-200",
    TELEMETRIA: "bg-amber-50 text-amber-700 border-amber-200",
    CUBAGEM: "bg-teal-50 text-teal-700 border-teal-200",
    RETORNO_ROMANEIO: "bg-blue-50 text-blue-700 border-blue-200",
    CONCLUSAO_ROMANEIO: "bg-purple-50 text-purple-700 border-purple-200",
};

const STATUS_AJUSTE = {
    PENDENTE: { label: "Pendente", cls: "bg-amber-100 text-amber-700 border-amber-200" },
    APROVADO: { label: "Aprovado", cls: "bg-success/10 text-success border-success/30" },
    REJEITADO: { label: "Rejeitado", cls: "bg-destructive/10 text-destructive border-destructive/30" },
};

// ─── Main Component ───────────────────────────────────────────────────────────
export default function Comissoes() {
    const { user } = useAuth();
    const isRoleComissao = user.perfis.includes("COMISSOES")

    const now = new Date();
    let anoInicial = now.getFullYear();
    let mesInicial = now.getMonth() + 1;
    if (now.getDate() >= 27) {
        if (mesInicial === 12) { mesInicial = 1; anoInicial += 1; }
        else { mesInicial += 1; }
    }

    const comissaoService = useMemo(() => new ComissaoService(), []);
    const filialService = useMemo(() => new FilialService(), []);
    const logService = useMemo(() => new LogService(), []);

    const [activeTab, setActiveTab] = useState("lista");
    const [selectedComissao, setSelectedComissao] = useState(null);
    const [loading, setLoading] = useState(true);
    const [modalCargasTable, setModalCargasTable] = useState(false);
    const [modalTelemetriaTable, setModalTelemetriaTable] = useState(false);
    const [modalParametros, setModalParametros] = useState(false);
    const [ano, setAno] = useState(anoInicial);
    const [mes, setMes] = useState(mesInicial);
    const [log, setLog] = useState({ data: null });
    const [modalAfastamentoTable, setModalAfastamentosTable] = useState(false);
    const [modalView, setModalView] = useState(false);
    const [modalAjustes, setModalAjustes] = useState(false);
    const [comissoes, setComissoes] = useState([]);
    const [filiais, setFiliais] = useState([]);
    const [filialId, setFilialId] = useState(null);

    useEffect(() => {
        buscarLogs();
    }, []);

    useEffect(() => {
        buscarComissoes();
        buscarFiliais();
    }, [ano, mes, filialId]);

    const buscarLogs = () => {
        logService.buscarLogPorModulo("GERACAO_COMISSAO").then(response => {
            setLog(response.data)
        }).catch(e => console.error(e))
    }

    const buscarFiliais = () => {
        filialService.listarComComissao(ano, mes).then(res => {
            setFiliais(res.data);
        });
    }

    const buscarComissoes = () => {
        setLoading(true);

        const data = {
            ano: ano,
            mes: mes,
            filialId: isRoleComissao ? user.filialId : filialId
        }

        comissaoService.listar(data)
            .then(res => setComissoes(res.data))
            .finally(() => setLoading(false));
    };

    // busca de novo as comissões do período e a data da última geração (útil depois de gerar em outra tela)
    const recarregar = () => {
        buscarComissoes();
        buscarFiliais();
        buscarLogs();
    };

    const onCloseModal = () => { setModalView(false); setSelectedComissao(null); };
    const onRowClick = (e) => { setSelectedComissao(e); setModalView(true); };
    const onFuncionarioSelected = (c) => {
        setSelectedComissao(c)
        setModalView(true)
    };

    return (
        <div className="bg-background ">
            {/* ── Header ── */}
            <div className="bg-card border-b border-border px-4 pt-4">
                <div className="flex flex-wrap justify-between gap-3 mb-3">
                    <div>
                        <h1 className="font-display text-xl font-bold text-foreground">Premiação de Comissões</h1>
                        <p className="text-sm text-muted-foreground">Relatório de Comissões de Motoristas e Auxiliares {log.data && <span className="text-xs fade-in">| Atualizado pela última vez {returnDate(log.data)}</span>} </p>
                    </div>

                    <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-2.5 shadow-card">

                        {!isRoleComissao &&
                            <div>
                                <select
                                    value={filialId ?? ""}
                                    onChange={e => setFilialId(e.target.value)}
                                    className="w-32 border-0 bg-transparent text-sm font-medium text-foreground focus:outline-none cursor-pointer"
                                >
                                    <option value="">
                                        Todas Filiais
                                    </option>
                                    {filiais.map(f => (
                                        <option key={f.id} value={f.id}>
                                            {f.nome}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        }

                        <i className="fa fa-calendar-alt text-muted-foreground text-sm mr-1" />
                        <select
                            value={mes}
                            onChange={e => setMes(e.target.value)}
                            className="w-32 border-0 bg-transparent text-sm font-medium text-foreground focus:outline-none cursor-pointer"
                        >
                            {MESES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                        </select>
                        <span className="text-muted-foreground">|</span>
                        <select
                            value={ano}
                            onChange={e => setAno(Number(e.target.value))}
                            className="border-0 bg-transparent text-sm font-medium text-foreground focus:outline-none cursor-pointer"
                        >
                            {ANOS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}

                        </select>
                    </div>

                    <button
                        type="button"
                        onClick={recarregar}
                        disabled={loading}
                        title="Recarregar as comissões do período"
                        className="flex h-[42px] items-center gap-2 rounded-xl border border-border bg-card px-4 text-sm font-medium text-foreground shadow-card transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        <i className={`fa fa-sync-alt text-muted-foreground ${loading ? "fa-spin" : ""}`} />
                        Recarregar
                    </button>
                    </div>
                </div>


                {/* Tabs */}
                <div className="flex flex-wrap gap-0 -mb-px">
                    {TABS.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`${(!user.perfis.includes("ADMIN") && tab.admin) && "d-none"} flex items-center gap-2 px-4 py-2.5 text-xs font-medium border-b-2 transition-all duration-150 whitespace-nowrap
                                ${activeTab === tab.id
                                    ? "border-primary text-primary bg-primary/5"
                                    : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"}`}
                        >
                            <i className={tab.icon} />
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* ── Tab Content ── */}
            <AnimatePresence mode="wait">
                {activeTab === "lista" && (
                    <motion.div key="lista" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                        <TabelaComissao
                            data={comissoes}
                            loading={loading}
                            onRowClick={onRowClick}
                        />
                    </motion.div>
                )}
                {activeTab === "dashboard" && (
                    <motion.div key="dashboard" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                        <DashboardTab comissoes={comissoes} onFuncionarioSelected={onFuncionarioSelected} />
                    </motion.div>
                )}
                {activeTab === "gerar" && (
                    <motion.div key="gerar" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                        <GerarComissoes ano={ano} mes={mes} onGerado={buscarComissoes} />
                    </motion.div>
                )}
                {activeTab === "ranking" && (
                    <motion.div key="ranking" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                        <RankingTab comissoes={comissoes} onRowSelected={onFuncionarioSelected} />
                    </motion.div>
                )}
                {activeTab === "romaneio" && (
                    <motion.div key="romaneio" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                        <TabelaRomaneios
                            ano={ano}
                            mes={mes}
                            filialId={isRoleComissao ? user.filialId : null}
                        />
                    </motion.div>
                )}
                {activeTab === "escalas" && (
                    <motion.div key="romaneio" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                        <TabelaCargaEntrega ano={ano} mes={mes} filialId={isRoleComissao ? user.filialId : filialId} />
                    </motion.div>
                )}
                {activeTab === "importacoes" && (
                    <motion.div key="importacoes" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                        <TabDiasImportados ano={ano} mes={mes} filialId={isRoleComissao ? user.filialId : filialId} />
                    </motion.div>
                )}
                {activeTab === "exportacao" && (
                    <motion.div key="exportacao" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                        <ExportacaoTab role={isRoleComissao} filialId={user.filialId} ano={ano} mes={mes} />
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ── Dialogs (inalterados) ── */}
            <Dialog
                header={
                    <div className="flex justify-between">
                        <div className="flex items-center gap-4">
                            <h1>{selectedComissao?.funcionarioNome ?? ""}</h1>
                            <span className="font-medium text-xs text-muted-foreground">{selectedComissao?.filialNome}</span>
                            <span className="font-medium text-xs text-muted-foreground">{selectedComissao?.funcionarioCargo}</span>
                            <span className="font-base text-xs text-muted-foreground">Código RH: {selectedComissao?.funcionarioCodigo}</span>
                        </div>
                        <div className="flex items-center gap-4 mx-2">
                            <span className="font-medium text-xs text-muted-foreground">
                                Atualizado em {formatLocalDateTime(selectedComissao?.dataAtualizacao) || '--/--'}
                            </span>
                        </div>
                    </div>
                }
                visible={modalView}
                modal
                maximizable
                className="w-[95%] min-h-[96%]"
                blockScroll
                onHide={onCloseModal}
                contentClassName="p-0"
                footer={
                    <div className="flex flex-wrap justify-between gap-2 pt-3 w-full">
                        <div className="flex flex-wrap gap-2">
                            <Button variant="white" iconLeft="fa fa-list" label="Mostrar Rotas" onClick={() => setModalCargasTable(true)} />
                            <Button variant="white" iconLeft="fa fa-user-times" label="Mostrar Afastamentos" onClick={() => setModalAfastamentosTable(true)} />
                            <Button variant="white" iconLeft="fa fa-shield" label="Mostrar Segurança Viária" onClick={() => setModalTelemetriaTable(true)} />
                            <Button variant="white" iconLeft="fa fa-wrench" label="Mostrar Ajustes" onClick={() => setModalAjustes(true)} />
                            <Button variant="white" title="Parâmetros Utilizados" iconLeft="fa fa-info-circle" onClick={() => setModalParametros(true)} />
                        </div>
                        <Button variant="gray" iconLeft="fa fa-times" label="Fechar" onClick={onCloseModal} />
                    </div>
                }
            >
                {selectedComissao && (
                    <div className="p-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-2">
                        <ResumoCard data={selectedComissao} />
                        <ConclusaoRotaCard data={selectedComissao} />
                        <CubagemCard data={selectedComissao} />
                        <RetornoCard data={selectedComissao} />
                        <TelemetriaCard data={selectedComissao} />
                        <DiasCard data={selectedComissao} />
                        <ResumoFinanceiroCard data={selectedComissao} />
                        <AuditoriaCard data={selectedComissao} />
                    </div>
                )}
            </Dialog>

            <Dialog
                header={`Ajustes da comissão${selectedComissao?.funcionarioNome ? " — " + selectedComissao.funcionarioNome : ""}`}
                visible={modalAjustes} modal className="w-[97%] md:w-[90%] min-h-[300px]"
                blockScroll onHide={() => setModalAjustes(false)}
            >
                {selectedComissao && <AjustesModal data={selectedComissao} />}
            </Dialog>

            <Dialog
                header="Parâmetros Utilizados"
                visible={modalParametros} modal className="w-[99%] min-h-[400px]"
                blockScroll onHide={() => setModalParametros(false)}
            >
                <ParametrosSnapshotView parametrosSnapshotJson={selectedComissao?.parametrosSnapshotJson} />
            </Dialog>

            <Dialog
                header={selectedComissao?.tipoDistribuidor === "LOGISTICO" &&
                    <div className="flex justify-between">
                        <div className="flex gap-4 font-semibold text-xs">
                            <Legenda label="Fundo em branco: Escala de Rotas" color="text-gray-100" />
                            <Legenda label="Fundo verde: Rota Greenmile" color="text-teal-200" />
                            <Legenda label="Fundo vermelho: Entregas de volumes SAP" color="text-red-100" />
                        </div>
                    </div>
                }
                headerClassName="p-2"
                visible={modalCargasTable}
                className="w-[97%]"
                focusOnShow={false}
                blockScroll
                onHide={() => setModalCargasTable(false)}
                contentClassName="p-0"
            >
                {selectedComissao && (
                    <div>
                        {selectedComissao.tipoDistribuidor === "LOGISTICO"
                            ? <TabelaCargaEntrega ano={ano} mes={mes} funcionarioId={selectedComissao.funcionarioId} onClose={() => setModalCargasTable(false)} />
                            : <TabelaRomaneios ano={ano} mes={mes} funcionarioId={selectedComissao.funcionarioId} onClose={() => setModalCargasTable(false)} />
                        }
                    </div>
                )}
            </Dialog>

            <Dialog
                header="Detalhes das Telemetrias"
                visible={modalTelemetriaTable} modal className="w-[95%]"
                blockScroll onHide={() => setModalTelemetriaTable(false)} contentClassName="p-0"
            >
                {selectedComissao && (
                    selectedComissao.tipoDistribuidor === "LOGISTICO"
                        ? <TabelaTelemetria data={selectedComissao} ano={ano} mes={mes} />
                        : <TabelaConcept data={selectedComissao} ano={ano} mes={mes} />
                )}
            </Dialog>

            <Dialog
                header="Detalhes Afastamentos/Faltas/Férias"
                visible={modalAfastamentoTable} modal className="w-[95%]"
                blockScroll onHide={() => setModalAfastamentosTable(false)} contentClassName="p-0"
            >
                {selectedComissao && <TabelaAfastamentos data={selectedComissao} ano={ano} mes={mes} />}
            </Dialog>
        </div>
    );
}

// ─── Cards (inalterados) ──────────────────────────────────────────────────────
function ResumoFinanceiroCard({ data }) {
    return (
        <div className="rounded-xl border border-accent bg-accent/5 p-4 shadow-card md:col-span-2">
            <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                <i className="fa fa-dollar text-teal-600" /> Resumo Financeiro
            </h4>
            <div className="grid grid-cols-3 gap-3 text-center">
                <FinanceBox label="Sem Desconto" value={formatarMoedaBrasileira(data.totalSemDesconto)} />
                <FinanceBox label="Descontos" value={formatarMoedaBrasileira(data.descontos)} />
                <div className="p-3 rounded-lg border-[0.5px] border-teal-500">
                    <p className="text-xs font-medium text-muted-foreground">Total a Receber</p>
                    <p className="text-lg font-extrabold text-teal-600">{formatarMoedaBrasileira(data.valorTotalReceber)}</p>
                </div>
            </div>
        </div>
    );
}
function FinanceBox({ label, value }) {
    return (
        <div className="p-3 rounded-lg border">
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="text-base font-bold text-foreground">{value}</p>
        </div>
    );
}
function TelemetriaCard({ data }) {
    const t = data ?? {};
    // telemetria só existe para motorista: o backend deixa tudo nulo para o auxiliar de entrega
    if (t.tipoDistribuidor === "LOGISTICO" && t.mediaSegurancaViaria == null && t.meta == null && t.atingiuMeta == null) {
        return (
            <CardWrapper title="Segurança Viária" icon="fa fa-shield">
                <p>Não se aplica a este cargo: somente o motorista é premiado por segurança viária.</p>
            </CardWrapper>
        );
    }
    return (
        <CardWrapper title="Segurança Viária" icon="fa fa-shield">
            <Item label="Média" value={formatPercent(t.mediaSegurancaViaria)} />
            <Item label="Meta" value={t.meta != null ? `${t.meta}%` : "—"} />
            <div className="flex justify-between items-center">
                <span>Status</span>
                <Tag value={t.atingiuMeta ? "Atingiu" : "Não atingiu"} severity={t.atingiuMeta ? "success" : "danger"} className="text-xs" />
            </div>
            <div className="border-t border-border pt-2 mt-2">
                <Item label="Valor com 1 Auxiliar" value={"R$ " + formatarMoedaBrasileira(t.valorCom1AuxTelemetria)} />
                <Item label="Valor com 2 Auxiliares" value={"R$ " + formatarMoedaBrasileira(t.valorCom2AuxTelemetria)} />
                <Item label="Valor Telemetria" value={formatarMoedaBrasileira(t.valorTotalTelemetria)} bold total />
            </div>
        </CardWrapper>
    );
}
function ResumoCard({ data }) {
    const g = data ?? {};
    const distLogistico = data.tipoDistribuidor === "LOGISTICO";
    return (
        <CardWrapper title="Resumo" icon="fa fa-check-circle">
            <Item label={distLogistico ? "Total Rotas - Escalas" : "Total de Rotas - Romaneios"} value={g.quantidadesEscalasDeRota ?? "—"} />
            {distLogistico ? <>
                <Item label="Total Rotas - Greenmile" value={g.totalRotas ?? "—"} />
                <Item label="Total Rotas - SAP" value={g.totalRotasCubagem ?? "—"} />
                <Item label="Total Rotas - Retorno" value={g.totalRotasComRetorno ?? "—"} />
            </> :
                <>
                    <Item label="Romaneios em Aberto" value={g.totalRomaneiosAbertos ?? "—"} />
                    <Item label="Romaneios Fechados" value={g.totalRomaneiosFechados ?? "—"} />
                    <Item label="Rotas Fretes" value={g.rotasFrete ?? "—"} />
                </>}
            <Item label="Rotas c/ 1 Auxiliar" value={g.percentual1Aux ? g.percentual1Aux + "% (" + g.rotas1Aux + ")" : "—"} />
            <Item label="Rotas c/ 2 Auxiliares" value={g.percentual2Aux ? g.percentual2Aux + "% (" + g.rotas2Aux + ")" : "—"} />
            {distLogistico && <Item label="Rotas 24h x 48h" value={g.rotas24Horas + '/' + g.rotas48Horas ?? "—"} />}
        </CardWrapper>
    );
}
function ConclusaoRotaCard({ data }) {
    const g = data ?? {};
    const distLogistico = data.tipoDistribuidor === "LOGISTICO";
    return (
        <CardWrapper title={distLogistico ? "Conclusão de Rota + Greenmile" : "Conclusão de Rota"} icon="fa fa-map-marker">
            <Item label="Total Rotas" value={g.totalRotas ?? "—"} />
            <Item label="Rotas no Prazo" value={g.rotasNoPrazo ?? "—"} />
            {distLogistico && <Item label="Com Imagem" value={g.rotasComImagem ?? "—"} />}
            <Item label={distLogistico ? "Rotas Ajustadas (já contadas como cumpridas)" : "Romaneios Ajustados (já contados como cumpridos)"} value={g.rotasAjustadas ?? 0} />
            <Item label="% Conclusão no Prazo" value={<span className="font-semibold">{formatPercent(g.percentualConclusao)}</span>} />
            {distLogistico && <Item label="% Adesão Greenmile" value={<span className="font-semibold text-teal-600">{formatPercent(g.percentualGreenmile)}</span>} />}
            <div className="border-t border-border pt-2 mt-2" />
            <Item label="Valor com 1 Auxiliar" value={"R$ " + formatarMoedaBrasileira(g.valorCom1AuxConclusao)} />
            <Item label="Valor com 2 Auxiliares" value={"R$ " + formatarMoedaBrasileira(g.valorCom2AuxConclusao)} />
            <Item label="Valor Total Conclusão" value={"R$ " + formatarMoedaBrasileira(g.valorTotalConclusao)} bold total />
        </CardWrapper>
    );
}
// O cálculo é feito só no backend: aqui apenas se apresenta o detalhamento que ele gravou (auditoriaJson).
function CubagemCard({ data }) {
    const [mostrarCalculo, setMostrarCalculo] = useState(false);
    const c = data ?? {};
    const auditoria = lerAuditoria(c.auditoriaJson);
    const buckets = (auditoria?.segmentos ?? []).flatMap(s => s.cubagem?.buckets ?? []);
    const totalCubos = (Number(c.cubosEntregue1Auxiliar) || 0) + (Number(c.cubosEntregue2Auxiliar) || 0);

    return (
        <CardWrapper title="Cubagem" icon="fa fa-cube">
            <Item label="Cubagem c/ 1 Auxiliar" value={safeNumber(c.cubosEntregue1Auxiliar)} />
            <Item label="Cubagem c/ 2 Auxiliares" value={safeNumber(c.cubosEntregue2Auxiliar)} />
            <Item label="Cubagem Freteiro" value={safeNumber(c?.cubosEntregueFrete)} />
            <Item label="Total Cubagem" value={<span className="font-semibold text-teal-600">{safeNumber(totalCubos)}</span>} />
            <div className="border-t border-border pt-2 mt-2">
                <Item label="Valor com 1 Auxiliar" value={'R$ ' + formatarMoedaBrasileira(c.valorCom1Auxiliar)} />
                <Item label="Valor com 2 Auxiliares" value={'R$ ' + formatarMoedaBrasileira(c.valorCom2Auxiliar)} />
                <Item label="Valor Total Cubagem" value={'R$ ' + formatarMoedaBrasileira(c.valorTotalCubagem)} bold total />
            </div>
            {buckets.length > 0 && (
                <div className="border-t border-border pt-2 mt-2 space-y-1.5">
                    <button
                        type="button"
                        onClick={() => setMostrarCalculo(v => !v)}
                        className="flex items-center gap-1.5 font-semibold text-foreground hover:underline"
                    >
                        <i className={`fa fa-chevron-${mostrarCalculo ? "up" : "down"} text-[10px]`} />
                        Como foi calculado
                    </button>
                    {mostrarCalculo && buckets.map((b, i) => (
                        <div key={i} className="rounded-md bg-muted/40 p-2 leading-snug">
                            <p className="font-medium text-foreground">{b.nome}</p>
                            <p>
                                ({safeNumber(b.cubosBrutos)} − {safeNumber(b.cubosRetornados)} retornados) = {safeNumber(b.cubosEfetivos)} cubos
                                × R$ {formatarValorPorCubo(b.valorPorCubo)} =R$ {formatarMoedaBrasileira(b.valorSemTeto)}
                            </p>
                            <p>
                                Teto R$ {formatarMoedaBrasileira(b.valorMaximo)}{b.tetoAplicado ? " (aplicado)" : ""} → <b>R$ {formatarMoedaBrasileira(b.valor)}</b>
                            </p>
                        </div>
                    ))}
                </div>
            )}
        </CardWrapper>
    );
}

const MOTIVOS_AUSENCIA = {
    FERIAS: "Férias",
    ATESTADO: "Atestado",
    AFASTAMENTO_INSS: "Afastamento INSS",
    LICENCA_OUTROS: "Licença/outros",
    FALTA: "Falta",
};

const FUNCOES = {
    MOTORISTA: "Motorista",
    AUXILIAR_ENTREGA: "Auxiliar de entrega",
};

function DiasCard({ data }) {
    const d = data ?? {};
    const dias = lerAuditoria(d.auditoriaJson)?.dias;
    const motivos = Object.entries(dias?.diasPorMotivo ?? {});
    const funcoes = Object.entries(dias?.diasPorFuncao ?? {});

    return (
        <CardWrapper title="Dias Trabalhados" icon="fa fa-calendar">
            {dias && <Item label="Período apurado" value={`${formatarDataIso(dias.janelaInicio)} a ${formatarDataIso(dias.janelaFim)}`} />}
            <Item label="Trabalhados" value={d.diasTrabalhados} />
            <Item label="Afastados" value={d.diasAfastados} />
            {motivos.map(([motivo, qtd]) => (
                <Item key={motivo} label={`  · ${MOTIVOS_AUSENCIA[motivo] ?? motivo}`} value={qtd} />
            ))}
            {dias?.diasForaVinculo > 0 && <Item label="Fora do vínculo (admissão/demissão)" value={dias.diasForaVinculo} />}
            <Item label="Total Período" value={d.totalDiasPeriodo} />
            {funcoes.length > 1 && funcoes.map(([funcao, qtd]) => (
                <Item key={funcao} label={`Dias como ${FUNCOES[funcao] ?? funcao}`} value={qtd} />
            ))}
            <div className="border-t border-border pt-2 mt-2">
                <Item label="Porcentual Trabalhado" value={formatPercent(d.porcentagemTrabalhado != null ? d.porcentagemTrabalhado * 100 : null)} bold />
            </div>
        </CardWrapper>
    );
}

function AuditoriaCard({ data }) {
    const auditoria = lerAuditoria(data?.auditoriaJson);
    if (!auditoria) return null;

    const segmentos = auditoria.segmentos ?? [];
    const avisos = auditoria.avisos ?? [];

    return (
        <div className="rounded-xl border border-border bg-card p-3 shadow-card md:col-span-2">
            <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                <i className="fa fa-search text-teal-600" /> Auditoria do Cálculo
            </h4>
            <div className="space-y-1.5 text-xs text-muted-foreground">
                <Item label="Período base da competência" value={`${formatarDataIso(auditoria.periodoBaseInicio)} a ${formatarDataIso(auditoria.periodoBaseFim)}`} />
                {auditoria.periodoPersonalizado && (
                    <Item label="Período personalizado" value={`${formatarDataIso(auditoria.janelaInicio)} a ${formatarDataIso(auditoria.janelaFim)}`} />
                )}
                <Item label="Admissão / Demissão" value={`${formatarDataIso(auditoria.dataAdmissao)} / ${formatarDataIso(auditoria.dataDemissao)}`} />
                {segmentos.map((s, i) => (
                    <div key={i} className="rounded-md bg-muted/40 p-2 leading-snug">
                        <p className="font-medium text-foreground">{FUNCOES[s.funcao] ?? s.funcao}</p>
                        <p>{s.diasEfetivos} dia(s) efetivos de {s.diasComoFuncao} na função · {s.cargas} escala(s)</p>
                        <p>
                            Bruto R$ {formatarMoedaBrasileira(s.totalSemDesconto)} × {s.diasEfetivos}/{auditoria.dias?.totalDiasPeriodo} = <b>R$ {formatarMoedaBrasileira(s.valorLiquido)}</b>
                        </p>
                        {s.retorno && (
                            <p>
                                Retorno: {formatPercent(s.retorno.percentualRetornoTotal)}
                                {s.retorno.faixaPremiacao ? ` · faixa ${s.retorno.faixaPremiacao}` : ""}
                                {s.retorno.ultrapassouLimite ? " · acima do limite (sem prêmio)" : ""}
                                {" · abate na cubagem: "}{safeNumber(s.retorno.totalCubosRetornadosDesconto)} cubos
                            </p>
                        )}
                    </div>
                ))}
                {avisos.map((a, i) => (
                    <p key={i} className="text-amber-600"><i className="fa fa-exclamation-triangle mr-1" />{a}</p>
                ))}
            </div>
        </div>
    );
}
function RetornoCard({ data }) {
    const r = data;
    return (
        <CardWrapper title="Retorno" icon="fa fa-repeat">
            <Item label="Cubos Ret. c/1 Auxiliar" value={safeNumber(r.totalCubosRetornadosCom1Aux)} />
            <Item label="Cubos Ret. c/2 Auxiliares" value={safeNumber(r.totalCubosRetornadosCom2Aux)} />
            <Item label="Total Retornado" value={<span className="font-semibold text-teal-600"> {safeNumber(r.totalCubosRetornados)} </span>} />
            <Item label="% Retorno" value={<span className={r.ultrapassouLimite ? "text-destructive font-bold" : ""}>{formatPercent(r.percentualRetornoTotal)}</span>}
                tooltip={'(' + r.totalCubosRetornados + ' / ' + r.totalCubosEntregues + ' ) x 100 = ' + ((r.totalCubosRetornados / r.totalCubosEntregues) * 100).toFixed(2)} />
            <Item label="Faixa" value={r.faixaPremiacao ?? "—"} />
            <div className="border-t border-border pt-2 mt-2" />
            <Item label="Valor com 1 Auxiliar" value={"R$ " + formatarMoedaBrasileira(r.valorCom1AuxRetorno)} />
            <Item label="Valor com 2 Auxiliares" value={"R$ " + formatarMoedaBrasileira(r.valorCom2AuxRetorno)} />
            <Item label="Valor Total Retorno" value={"R$ " + formatarMoedaBrasileira(r.valorTotalRetorno)} bold total />
        </CardWrapper>
    );
}

// Ajustes do funcionário no período da comissão, como o backend os usou no cálculo (nada é recalculado aqui).
function AjustesModal({ data }) {
    const auditoria = lerAuditoria(data?.auditoriaJson);

    if (!auditoria) {
        return (
            <p className="p-4 text-sm text-muted-foreground">
                Esta comissão não tem o detalhamento de ajustes (foi gerada antes dele existir). Gere a comissão novamente para vê-lo.
            </p>
        );
    }

    const ajustes = auditoria.ajustes ?? [];
    const aplicados = ajustes.filter(a => a.aplicado).length;
    const periodo = `${formatarDataIso(auditoria.janelaInicio)} a ${formatarDataIso(auditoria.janelaFim)}`;

    const indicadores = [
        { nome: "Retorno", base: baseDoIndicador(auditoria, "retorno") },
        { nome: "Conclusão de Rota + Green Mile", base: baseDoIndicador(auditoria, "conclusao") },
        { nome: "Segurança Viária", base: baseDoIndicador(auditoria, "telemetria") },
    ].filter(i => i.base && i.base.diasExcluidos.length > 0);

    return (
        <div className="space-y-3 p-1">
            <p className="text-xs text-muted-foreground">
                Período da comissão: <b>{periodo}</b> · {ajustes.length} ajuste(s) encontrado(s), {aplicados} aplicado(s) ao cálculo.
                Ajuste aprovado retira o dia do indicador (não vira zero) e não afeta os demais indicadores. Exceção: na conclusão de rota (Green Mile no DL, acerto no DA) a rota ajustada continua no total e conta como cumprida.
            </p>

            {indicadores.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                    {indicadores.map(i => (
                        <div key={i.nome} className="rounded-lg border border-border bg-muted/30 p-2">
                            <p className="font-semibold text-foreground">{i.nome}</p>
                            <p>Dias avaliáveis: <b>{i.base.diasAvaliaveis}</b></p>
                            <p>Dias excluídos por ajuste: <b>{i.base.diasExcluidos.length}</b> ({i.base.diasExcluidos.map(formatarDataIso).join(", ")})</p>
                        </div>
                    ))}
                </div>
            )}

            {ajustes.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">Nenhum ajuste cadastrado para este funcionário no período.</p>
            ) : (
                <div className="overflow-x-auto rounded-lg border border-border">
                    <table className="w-full text-xs">
                        <thead className="bg-muted/50 text-left text-muted-foreground">
                            <tr>
                                <th className="px-2 py-2">Data</th>
                                <th className="px-2 py-2">Ajuste</th>
                                <th className="px-2 py-2">Transporte</th>
                                <th className="px-2 py-2">Status</th>
                                <th className="px-2 py-2">Impacto</th>
                                <th className="px-2 py-2">Motivo</th>
                                <th className="px-2 py-2">Solicitado por</th>
                                <th className="px-2 py-2">Aprovado por</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ajustes.map((a, i) => {
                                const st = STATUS_AJUSTE[a.status] ?? { label: a.status, cls: "bg-muted text-muted-foreground border-border" };
                                return (
                                    <tr key={`${a.ajusteId}-${a.data}-${a.indicador}-${i}`} className="border-t border-border align-top">
                                        <td className="px-2 py-2 whitespace-nowrap">{formatarDataIso(a.data)}</td>
                                        <td className="px-2 py-2">
                                            <span className={`inline-block rounded border px-2 py-0.5 font-semibold ${INDICADOR_COR[a.indicador] ?? ""}`}>
                                                {a.indicadorNome}
                                            </span>
                                            {a.detalhe && <p className="mt-1 text-muted-foreground">{a.detalhe}</p>}
                                            {a.funcao && <p className="text-muted-foreground">{FUNCOES[a.funcao] ?? a.funcao}</p>}
                                        </td>
                                        <td className="px-2 py-2">{a.transporte ?? "—"}</td>
                                        <td className="px-2 py-2">
                                            <span className={`inline-block rounded-full border px-2 py-0.5 font-semibold ${st.cls}`}>{st.label}</span>
                                            <p className={`mt-1 font-semibold ${a.aplicado ? "text-success" : "text-muted-foreground"}`}>
                                                {a.aplicado ? "Aplicado" : "Não aplicado"}
                                            </p>
                                        </td>
                                        <td className="px-2 py-2 min-w-[220px]">{a.impacto}</td>
                                        <td className="px-2 py-2">{a.motivo ?? "—"}</td>
                                        <td className="px-2 py-2">
                                            {a.solicitadoPor ?? "—"}
                                            {a.dataSolicitacao && <p className="text-muted-foreground">{formatLocalDateTime(a.dataSolicitacao)}</p>}
                                        </td>
                                        <td className="px-2 py-2">
                                            {a.aprovadoPor ?? "—"}
                                            {a.dataAprovacao && <p className="text-muted-foreground">{formatLocalDateTime(a.dataAprovacao)}</p>}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function CardWrapper({ title, icon, children }) {
    return (
        <div className="rounded-xl border border-border bg-card p-3 shadow-card">
            <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                <i className={`${icon} text-teal-600`} /> {title}
            </h4>
            <div className="space-y-1.5 text-xs text-muted-foreground">{children}</div>
        </div>
    );
}

function Item({ label, value, bold, total, tooltip }) {
    return (
        <div className="flex justify-between relative group ">
            <span>{label}</span>

            <span className={total ? "font-bold text-teal-600 cursor-default" : bold ? "font-bold text-foreground cursor-default" : "font-medium text-foreground cursor-default"}>
                {value}
            </span>

            {tooltip && (
                <div className="
                    absolute right-0 top-full mt-1
                    hidden group-hover:block
                    bg-foreground text-background text-xs
                    px-2 py-1 rounded-md shadow-pop
                    whitespace-pre-line z-10
                ">
                    {tooltip}
                </div>
            )}
        </div>
    );
}

function Legenda({ label, color }) {
    return (
        <div className="flex gap-2">
            <i className={`fa fa-circle ${color}`} /> {label}
        </div>
    );
}

const TABS = [
    { id: "lista", label: "Lista de Comissões", icon: "fa fa-list" },
    { id: "dashboard", label: "Dashboard", icon: "fa fa-bar-chart" },
    { id: "ranking", label: "Ranking", icon: "fa fa-trophy" },
    { id: "romaneio", label: "Romaneios (DA)", icon: "fa fa-box" },
    { id: "escalas", label: "Escalas de Rota (DL)", icon: "fa fa-tasks" },
    { id: "importacoes", label: "Dias Importados", icon: "fa fa-calendar-check" },
    { id: "exportacao", label: "Exportações", icon: "fa fa-file-export" },
    { id: "gerar", label: "Gerar Comissões", icon: "fa fa-cogs", admin: true },
];